import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { aboutData } from "../data/mock";
import {
  ArrowRight,
  Target,
  FileCode2,
  Boxes,
  ShieldCheck,
  Workflow,
  Award,
} from "lucide-react";
import { TechStackGrid } from "../components/brand/TechLogos";
import { DataFlowAnimation } from "../components/animations/DataFlowAnimation";
import { Reveal } from "../components/animations/Reveal";

const philosophyIcons = {
  "Infrastructure as Code, By Default": FileCode2,
  "Automation With a Target": Workflow,
  "Security Matched to the Threat Model": ShieldCheck,
  "Depth Over Breadth": Target,
};

const credentialIcons = { Award, Boxes, ShieldCheck, Workflow };

export default function About() {
  return (
    <div className="bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28 border-b border-border">
        <div className="absolute inset-0 graph-bg" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-primary text-sm font-mono uppercase tracking-wider mb-4">About Grafodata</p>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight mb-6">
              Cloud Architecture for Data That Carries Real Consequences
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Built by a cloud architect who chose depth over breadth — three industries, four
              core disciplines, no generic playbook.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 lg:py-28 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">Our Mission</h2>
              <p className="text-xl text-foreground/90 leading-relaxed mb-8 font-light">
                "{aboutData.mission}"
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {aboutData.whyWeExist}
              </p>
            </Reveal>

            <Reveal delay={120} className="grid grid-cols-2 gap-4">
              {aboutData.credentials.map((item, i) => {
                const Icon = credentialIcons[item.icon] || Award;
                return (
                  <div key={i} className="p-6 rounded-2xl bg-card border border-border">
                    <Icon className="w-8 h-8 text-primary mb-4" />
                    <p className="text-muted-foreground text-sm mb-1">{item.label}</p>
                    <p className="text-foreground font-semibold">{item.value}</p>
                  </div>
                );
              })}
            </Reveal>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 lg:py-28 bg-card/40 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Our Philosophy</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              The principles that guide every engagement, regardless of industry
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {aboutData.philosophy.map((item, index) => {
              const Icon = philosophyIcons[item.title] || Target;
              return (
                <Reveal key={index} delay={index * 100}>
                  <Card className="bg-card border-border hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 group h-full">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-5">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                          <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data Pipeline */}
      <section className="py-16 lg:py-20 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              How We Think About a Pipeline
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The same shape, whether the source is a PLC, a fleet uplink, or a betting engine
            </p>
          </Reveal>
          <DataFlowAnimation />
        </div>
      </section>

      {/* Tech Expertise */}
      <section className="py-20 lg:py-28 bg-card/40 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Technology Expertise
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Deep expertise across cloud platforms, orchestration, and automation technologies
            </p>
          </Reveal>
          <TechStackGrid />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="relative overflow-hidden rounded-3xl bg-card border border-border p-12 lg:p-16 text-center">
            <div className="absolute inset-0 graph-bg-dense opacity-50" />
            <div className="relative">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Let's Build the Infrastructure Layer
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
                Ready to bring real engineering discipline to your SCADA, ADAS/AV, or iGaming data?
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 h-12">
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
